import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, ClipboardList, Package, Users, LogOut, Menu, Settings as SettingsIcon, BarChart3, Building2, Wallet } from "lucide-react";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import LogoMark from "@/components/LogoMark";
import api, { setBranch } from "@/lib/api";

const BrandMark = () => (
  <div className="flex items-center gap-3" data-testid="brand-mark">
    <LogoMark size={44} className="shadow-[0_4px_14px_rgba(212,175,55,0.4)]" />
    <div className="leading-tight">
      <p className="font-serif text-xl font-bold text-white tracking-tight">Rizqia</p>
      <p className="text-[11px] uppercase tracking-[0.25em] text-brand-gold">Aqiqah</p>
    </div>
  </div>
);

const navItems = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/pesanan", label: "Pesanan", icon: ClipboardList },
  { to: "/paket", label: "Daftar Harga", icon: Package, roles: ["admin", "staff"] },
  { to: "/kas", label: "Laporan Keuangan", icon: Wallet, roles: ["staff"] },
  { to: "/laporan", label: "Laporan Keuangan", icon: BarChart3, roles: ["admin", "keuangan", "super_admin"] },
  { to: "/kas", label: "Buku Kas Harian", icon: Wallet, roles: ["admin", "keuangan", "super_admin"] },
  { to: "/cabang", label: "Cabang", icon: Building2, roles: ["super_admin"] },
  { to: "/pengguna", label: "Pengguna", icon: Users, roles: ["admin", "super_admin"] },
  { to: "/pengaturan", label: "Pengaturan", icon: SettingsIcon, roles: ["admin", "super_admin", "staff"] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [branches, setBranches] = useState([]);
  const isSuper = user?.role === "super_admin";
  const canSwitchBranch = isSuper || user?.role === "keuangan";

  useEffect(() => {
    if (canSwitchBranch) api.get("/branches").then(({ data }) => setBranches(data)).catch(() => {});
  }, [canSwitchBranch]);

  const activeBranch = localStorage.getItem("ra_branch") || "";
  const onBranchChange = (id) => { setBranch(id); window.location.reload(); };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const links = navItems.filter((n) => !n.roles || n.roles.includes(user?.role));

  const SidebarInner = () => (
    <div className="flex h-full flex-col bg-brand-espresso relative overflow-hidden">
      <div className="absolute inset-0 pattern-dots opacity-40 pointer-events-none" />
      <div className="relative z-10 px-6 py-7 border-b border-white/10">
        <BrandMark />
      </div>
      <nav className="relative z-10 flex-1 space-y-1 px-4 py-6">
        {canSwitchBranch && (
          <div className="mb-3 px-1" data-testid="branch-selector">
            <p className="mb-1 px-1 text-[10px] font-semibold uppercase tracking-wide text-white/50">Cabang Aktif</p>
            <select value={activeBranch} onChange={(e) => onBranchChange(e.target.value)}
              className="w-full rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-brand-gold">
              <option value="" className="text-brand-espresso">Semua Cabang</option>
              {branches.map((b) => (<option key={b.id} value={b.id} className="text-brand-espresso">{b.name} ({b.code})</option>))}
            </select>
          </div>
        )}
        {links.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={() => setOpen(false)}
            data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-colors duration-200 ${
                isActive
                  ? "bg-brand-gold/15 text-brand-gold border-l-2 border-brand-gold"
                  : "text-white/70 hover:bg-white/5 hover:text-white border-l-2 border-transparent"
              }`
            }
          >
            <Icon className="h-[18px] w-[18px]" />
            {label}
          </NavLink>
        ))}
      </nav>
      <div className="relative z-10 border-t border-white/10 p-4">
        <div className="mb-3 px-2">
          <p className="truncate text-sm font-semibold text-white">{user?.name}</p>
          <p className="truncate text-xs text-white/50">{user?.email}</p>
          <span className="mt-1 inline-block rounded-full bg-brand-gold/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand-gold">
            {user?.role}
          </span>
        </div>
        <Button
          variant="ghost"
          onClick={handleLogout}
          data-testid="logout-btn"
          className="w-full justify-start gap-3 text-white/70 hover:bg-white/5 hover:text-white"
        >
          <LogOut className="h-[18px] w-[18px]" /> Keluar
        </Button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-brand-bg">
      {/* Desktop sidebar */}
      <aside className="hidden w-72 shrink-0 lg:block">
        <div className="fixed h-screen w-72">
          <SidebarInner />
        </div>
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-72">
            <SidebarInner />
          </div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-brand-line bg-brand-bg/90 px-4 py-3 backdrop-blur lg:hidden no-print">
          <button onClick={() => setOpen(true)} data-testid="mobile-menu-btn" className="rounded-lg p-2 hover:bg-brand-surface">
            <Menu className="h-5 w-5 text-brand-espresso" />
          </button>
          <span className="font-serif text-lg font-bold text-brand-espresso">Rizqia Aqiqah</span>
        </header>
        <main className="flex-1">{children}</main>
      </div>
    </div>
  );
}
